import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Category } from '~app/modules/generic/models';
import { APP_CATEGORY_ROUTE, NUMBER_32 } from '~shared/constants';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { NavigationApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationNavigationEventType } from '~shared/modules/application-event/constants';
import { environment } from '~env/environment';
import { CategoryImagesUtil } from '~app/modules/catalog/utils/category-images.util';
import {
  IMGIX_CATEGORY_PLP_PAGE_IMAGE_HEIGHT,
  IMGIX_CATEGORY_PLP_PAGE_IMAGE_WIDTH,
  MAX_CARD_COUNT_TO_SHOW_MOBILE,
} from '~app/modules/catalog/constants';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';

const showAll = 'click to show all';

@Component({
  selector: 'app-search-category-children',
  templateUrl: './category-children.component.html',
  styleUrls: ['./category-children.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryChildrenComponent implements AfterViewInit, OnChanges {
  @Input() categories: Category[];
  @Input() isMobileOrTablet: boolean;
  @Output() showAllClick = new EventEmitter();

  @ViewChild('categoryChildrenContainer') categoryChildrenContainer: ElementRef;

  categoryRoute = `/${APP_CATEGORY_ROUTE}`;
  defaultCategoryImage = `${environment.cdnBaseUrl()}/images/product-catalog-placeholder.png`;
  imageWidthAndHeight = NUMBER_32;
  isShowAllButtonVisible: boolean;
  lastCategoryToShowIndex: number;
  categoriesCalculationIsDone: boolean;
  showAllCategories = false;

  constructor(
    private applicationEventService: ApplicationEventService,
    private productsListUrlService: ProductsListUrlService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['categories']) {
      this.setDefaultState();
      this.checkCategoryContainerSize();
    }
  }

  ngAfterViewInit(): void {
    this.setDefaultState();
    this.checkCategoryContainerSize();
  }

  getSource(category: Category): string {
    return CategoryImagesUtil.getCategoryImageSrc(
      category.id,
      IMGIX_CATEGORY_PLP_PAGE_IMAGE_HEIGHT,
      IMGIX_CATEGORY_PLP_PAGE_IMAGE_WIDTH
    );
  }

  getSourceSet(category: Category): string {
    return CategoryImagesUtil.getCategoryImageSrcSet(
      category.id,
      IMGIX_CATEGORY_PLP_PAGE_IMAGE_HEIGHT,
      IMGIX_CATEGORY_PLP_PAGE_IMAGE_WIDTH
    );
  }

  onSelectCategory(category: Category): void {
    this.productsListUrlService.subCategoryId = category.id;
    this.applicationEventService.publish(
      new NavigationApplicationEvent(ApplicationNavigationEventType.PLPCategoryNavigation, null, null, {
        categoryName: category.name,
      })
    );
  }

  onClickShowAll(): void {
    this.isShowAllButtonVisible = false;
    this.showAllCategories = true;
    this.lastCategoryToShowIndex = this.categories.length;
    this.applicationEventService.publish(
      new NavigationApplicationEvent(ApplicationNavigationEventType.PLPCategoryNavigationExpanded, null, null, {
        showAllText: showAll,
      })
    );
    this.showAllClick.emit();
  }

  private checkCategoryContainerSize(): void {
    //The element is not rendered
    if (!this.categoryChildrenContainer) {
      return;
    }

    if (this.isMobileOrTablet) {
      this.isShowAllButtonVisible = MAX_CARD_COUNT_TO_SHOW_MOBILE + 1 < this.categories.length;
      this.lastCategoryToShowIndex = MAX_CARD_COUNT_TO_SHOW_MOBILE;
    } else {
      const pixelPerLetter = 7;
      const paddingAndMargin = 40;
      const showAllButtonWidth = 73;
      const containerWidth = this.categoryChildrenContainer.nativeElement.offsetWidth;
      let categoriesElementsWidthSum = 0;

      for (const category of this.categories) {
        categoriesElementsWidthSum +=
          category.name.length * pixelPerLetter + paddingAndMargin + this.imageWidthAndHeight;

        if (containerWidth <= categoriesElementsWidthSum + showAllButtonWidth) {
          this.lastCategoryToShowIndex = this.categories.findIndex((c) => c.id === category.id);
          this.isShowAllButtonVisible = true;
          break;
        }
      }
    }

    this.categoriesCalculationIsDone = true;
    this.cd.detectChanges();
  }

  private setDefaultState(): void {
    this.categoriesCalculationIsDone = false;
    this.showAllCategories = false;
    this.isShowAllButtonVisible = false;
    this.lastCategoryToShowIndex = Number.POSITIVE_INFINITY;
  }

  trackById(index, category) {
    return category.id;
  }
}
